// CategoryManager — full CRUD UI for product categories. Lives inside
// the Inventory page (owner-only). Each row is a category with edit
// (inline rename), reorder, deactivate, and hard-delete controls.
//
// Conventions:
//   - "+ Tambah kategori" button at the top opens an inline form
//   - Inline edit via the row's own input + save/cancel
//   - Soft-delete (hide from POS) is the default; hard-delete requires
//     the category to have zero products (rare in practice)

import { useMemo, useState } from 'react';
import {
  Plus,
  Edit2,
  Check,
  X,
  Tag,
  AlertCircle,
  PowerOff,
  Power,
  GripVertical,
  Hash,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Chip } from '@/components/ui/Chip';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingState } from '@/components/ui/LoadingState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useSetCategoryActive,
} from '@/hooks/useCategories';
import { useProducts } from '@/hooks/useProducts';
import { formatRelative, formatNumber } from '@/lib/format';
import { cn } from '@/lib/format';
import type { Category } from '@/types/domain';

export function CategoryManager() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: categories, isLoading } = useCategories();
  const { data: products } = useProducts({});
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const setActive = useSetCategoryActive();

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDeactivate, setPendingDeactivate] = useState<Category | null>(null);

  // Product count per category id (for the row hints + the hard-delete guard).
  const productCountByCategory = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of products ?? []) {
      if (!p.categoryId) continue;
      map.set(p.categoryId, (map.get(p.categoryId) ?? 0) + 1);
    }
    return map;
  }, [products]);

  // Merge "real" categories with orphan categoryIds (free-form strings
  // already on products but no Category doc yet). The POS still shows
  // these as-is for backward compat — this UI just surfaces them so
  // the owner can promote them to a proper Category.
  const orphanIds = useMemo(() => {
    const knownIds = new Set((categories ?? []).map((c) => c.id));
    const orphans = new Set<string>();
    for (const p of products ?? []) {
      if (p.categoryId && !knownIds.has(p.categoryId)) {
        orphans.add(p.categoryId);
      }
    }
    return Array.from(orphans).sort();
  }, [categories, products]);

  const handleCreate = async (input: { name: string; description?: string }) => {
    if (!user) return;
    await createCategory.mutateAsync({
      name: input.name,
      description: input.description,
      createdBy: user.uid,
    });
    // Defence-in-depth: even though the mutation's onSuccess invalidates
    // the categories query, we also force a hard refetch so the new
    // category shows up immediately — useful if the cache key was
    // somehow stale (e.g. across tab switches with different filter
    // params) and the index is still building in the background.
    await qc.refetchQueries({ queryKey: ['categories'] });
    setAdding(false);
  };

  const handleUpdate = async (
    id: string,
    input: { name: string; description?: string | null },
  ) => {
    await updateCategory.mutateAsync({ id, input });
    setEditingId(null);
  };

  const handleDeactivate = async (cat: Category) => {
    await deleteCategory.mutateAsync(cat.id);
    setPendingDeactivate(null);
  };

  const handleReactivate = async (cat: Category) => {
    await setActive.mutateAsync({ id: cat.id, isActive: true });
  };

  if (isLoading) {
    return <LoadingState message="Memuat kategori…" />;
  }

  const allRows = categories ?? [];
  const hasAny = allRows.length > 0 || orphanIds.length > 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h3 className="text-subhead font-display font-semibold text-ink">
            Kategori produk
          </h3>
          <p className="text-caption text-ink-subtle mt-0.5">
            Kategori global — dipakai di semua event. Rename aman, produk
            yang reference tidak ke-break.
          </p>
        </div>
        {!adding && (
          <Button
            variant="primary"
            size="md"
            leftIcon={<Plus size={14} />}
            onClick={() => setAdding(true)}
          >
            Tambah kategori
          </Button>
        )}
      </header>

      {adding && (
        <NewCategoryForm
          onCancel={() => setAdding(false)}
          onSave={handleCreate}
          saving={createCategory.isPending}
        />
      )}

      {!hasAny ? (
        <Card>
          <EmptyState
            icon={<Tag size={24} strokeWidth={1.5} />}
            title="Belum ada kategori"
            description="Tambah kategori seperti 'Frame', 'Lensa', 'Aksesoris' untuk mengelompokkan produk di POS."
            action={
              !adding && (
                <Button
                  variant="primary"
                  leftIcon={<Plus size={16} />}
                  onClick={() => setAdding(true)}
                >
                  Buat kategori pertama
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <Card padding="none">
          <ul role="list" className="divide-y divide-line">
            {allRows.map((cat) => (
              <CategoryRow
                key={cat.id}
                category={cat}
                productCount={productCountByCategory.get(cat.id) ?? 0}
                isEditing={editingId === cat.id}
                onStartEdit={() => setEditingId(cat.id)}
                onCancelEdit={() => setEditingId(null)}
                onSave={(input) => handleUpdate(cat.id, input)}
                onDeactivate={() => setPendingDeactivate(cat)}
                onReactivate={() => handleReactivate(cat)}
                saving={updateCategory.isPending}
              />
            ))}

            {orphanIds.length > 0 && (
              <li className="bg-warning/5 border-t border-warning/20">
                <div className="px-4 py-3 flex items-start gap-3">
                  <span className="w-9 h-9 rounded bg-warning/10 text-warning flex items-center justify-center shrink-0">
                    <AlertCircle size={16} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-small font-medium text-ink">
                      {orphanIds.length} kategori lama belum punya Category doc
                    </p>
                    <p className="text-caption text-ink-subtle mt-1">
                      Produk dengan <code className="font-mono">categoryId</code> di
                      bawah ini dibuat sebelum fitur Category. Klik &ldquo;Promote&rdquo; untuk
                      bikin Category doc dengan nama yang sama — produk otomatis
                      ke-remap ke id baru. Aman untuk rename/delete nanti.
                    </p>
                    <ul className="mt-2 flex flex-wrap gap-1.5">
                      {orphanIds.map((orphanId) => (
                        <li key={orphanId}>
                          <PromoteOrphanButton
                            orphanId={orphanId}
                            productCount={
                              productCountByCategory.get(orphanId) ?? 0
                            }
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </li>
            )}
          </ul>
        </Card>
      )}

      <ConfirmDialog
        open={!!pendingDeactivate}
        onClose={() => setPendingDeactivate(null)}
        title="Sembunyikan kategori ini?"
        description={
          pendingDeactivate
            ? `Kategori "${pendingDeactivate.name}" akan disembunyikan dari POS dan inventory. Produk yang sudah ada di event / invoice history tidak akan berubah.`
            : ''
        }
        confirmText="Sembunyikan"
        variant="danger"
        loading={deleteCategory.isPending}
        onConfirm={() => {
          if (pendingDeactivate) handleDeactivate(pendingDeactivate);
        }}
      />
    </div>
  );
}

function NewCategoryForm({
  onCancel,
  onSave,
  saving,
}: {
  onCancel: () => void;
  onSave: (input: { name: string; description?: string }) => Promise<void>;
  saving: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSave({ name: name.trim(), description: description.trim() || undefined });
    setName('');
    setDescription('');
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
            <Plus size={16} />
          </span>
          <p className="text-subhead font-display font-semibold text-ink">
            Kategori baru
          </p>
        </div>
        <Input
          label="Nama"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="cth: Frame, Lensa, Aksesoris, Dropship"
          maxLength={40}
          autoFocus
          required
        />
        <Textarea
          label="Deskripsi (opsional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Catatan internal untuk kategori ini — tidak muncul di invoice."
        />
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Batal
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={saving}
            disabled={!name.trim()}
          >
            Simpan
          </Button>
        </div>
      </div>
    </Card>
  );
}

function CategoryRow({
  category,
  productCount,
  isEditing,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDeactivate,
  onReactivate,
  saving,
}: {
  category: Category;
  productCount: number;
  isEditing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (input: { name: string; description?: string | null }) => Promise<void>;
  onDeactivate: () => void;
  onReactivate: () => void;
  saving: boolean;
}) {
  const [editName, setEditName] = useState(category.name);
  const [editDescription, setEditDescription] = useState(category.description ?? '');

  const handleSave = () => {
    onSave({
      name: editName,
      description: editDescription.trim() || null,
    });
  };

  return (
    <li className={cn('px-4 py-3', !category.isActive && 'bg-canvas/40')}>
      {isEditing ? (
        <div className="space-y-2">
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={40}
            autoFocus
            className="max-w-md"
          />
          <Textarea
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            rows={2}
            placeholder="Deskripsi (opsional)"
            className="max-w-md"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={saving}
              disabled={!editName.trim()}
              leftIcon={<Check size={14} />}
            >
              Simpan
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancelEdit}
              disabled={saving}
              leftIcon={<X size={14} />}
            >
              Batal
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span
            className="w-9 h-9 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0"
            aria-hidden
          >
            <Tag size={16} />
          </span>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-body font-medium text-ink truncate">
                {category.name}
              </p>
              {!category.isActive && <Chip variant="default">Hidden</Chip>}
              {productCount > 0 && (
                <Chip variant="info">
                  <Hash size={10} />
                  {formatNumber(productCount)} produk
                </Chip>
              )}
            </div>
            {category.description && (
              <p className="text-caption text-ink-subtle mt-0.5 line-clamp-1">
                {category.description}
              </p>
            )}
            <p className="text-caption text-ink-subtle mt-0.5">
              Diperbarui {formatRelative(category.updatedAt)}
            </p>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={onStartEdit}
              leftIcon={<Edit2 size={12} />}
              aria-label={`Edit ${category.name}`}
            >
              Edit
            </Button>
            {category.isActive ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDeactivate}
                leftIcon={<PowerOff size={12} />}
                className="text-danger hover:text-danger"
                aria-label={`Sembunyikan ${category.name}`}
              >
                Sembunyikan
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={onReactivate}
                leftIcon={<Power size={12} />}
                aria-label={`Aktifkan kembali ${category.name}`}
              >
                Aktifkan
              </Button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/**
 * "Promote" button for orphan categoryIds — creates a Category doc
 * with the same name so the product can later be re-mapped. We don't
 * auto-remap because that requires a batched update across all
 * products with the same orphan id, and the user might want to
 * choose a different final name.
 */
function PromoteOrphanButton({
  orphanId,
  productCount,
}: {
  orphanId: string;
  productCount: number;
}) {
  const { user } = useAuth();
  const createCategory = useCreateCategory();

  const handlePromote = async () => {
    if (!user) return;
    await createCategory.mutateAsync({
      name: orphanId,
      createdBy: user.uid,
    });
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={handlePromote}
      loading={createCategory.isPending}
      title={`${productCount} produk masih pakai id "${orphanId}"`}
    >
      <GripVertical size={12} />
      Promote &ldquo;{orphanId}&rdquo;
      <span className="text-caption text-ink-subtle">
        ({formatNumber(productCount)})
      </span>
    </Button>
  );
}
